import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Avatar from "@/components/Avatar";
import { ArrowUpRight, Sprout } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Public, unauthenticated page that renders a single Colleague Card.
// The URL pattern is /c/:slug
export default function PublicCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/cards/${slug}`);
        if (!active) return;
        setCard(data);
        setStatus("ok");
        document.title = `${data.persona_name} · Colleague`;
      } catch (e) {
        if (!active) return;
        if (e?.response?.status === 404) setStatus("notfound");
        else setStatus("error");
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF9F6] text-[#6B655D]">
        Loading…
      </div>
    );
  }
  if (status === "notfound") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBF9F6] text-center p-8">
        <h1 className="font-serif text-4xl text-[#2D2926] mb-3">This card isn't here</h1>
        <p className="text-[#6B655D] mb-6">
          The Colleague Card you're looking for may have been unpublished.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-2.5 text-sm transition-colors"
        >
          Visit Colleague <ArrowUpRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    );
  }
  if (status === "error" || !card) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF9F6] text-[#6B655D]">
        Something went wrong.
      </div>
    );
  }

  return (
    <div data-testid="public-card-page" className="min-h-screen bg-[#FBF9F6]">
      {/* slim header */}
      <header className="border-b border-[#E6E2D8]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#2D2926]">
            <Sprout className="text-[#D4A574]" size={20} strokeWidth={1.5} />
            <span className="font-serif text-xl tracking-tight">Colleague</span>
          </Link>
          <Link
            to="/register"
            data-testid="public-card-cta-top"
            className="text-sm bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-4 py-2 transition-colors"
          >
            Build yours
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <div className="anim-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A] mb-4">
            Meet a Colleague
          </p>
          <div className="flex items-center gap-5 mb-8">
            <Avatar
              src={card.avatar_url}
              name={card.persona_name}
              size={96}
              className="shadow-[0_8px_30px_-6px_rgba(45,41,38,0.15)]"
            />
            <div className="min-w-0">
              <h1 className="font-serif text-5xl text-[#2D2926] tracking-tight leading-none">
                {card.persona_name}
              </h1>
              <p className="text-sm uppercase tracking-wider text-[#6B655D] mt-2">
                {card.persona_role}
                {card.persona_industry ? ` · ${card.persona_industry}` : ""}
              </p>
            </div>
          </div>

          {card.tagline_user && (
            <p className="text-xl italic text-[#2D2926]/80 mb-6 leading-relaxed">
              "{card.tagline_user}"
            </p>
          )}

          <blockquote
            data-testid="card-story"
            className="font-serif text-3xl sm:text-4xl text-[#2D2926] leading-snug border-l-4 border-[#D4A574] pl-6 my-10"
          >
            {card.story_oneliner}
          </blockquote>

          {card.owner_name && (
            <p className="text-[#2D2926] mb-12">
              — {card.owner_name}, on working with {card.persona_name.split(" ")[0]}
            </p>
          )}

          {/* Traits & expertise */}
          {(card.personality_traits?.length > 0 ||
            card.expertise_areas?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-16">
              {card.personality_traits?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#9A938A] mb-3">
                    Personality
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {card.personality_traits.map((t) => (
                      <span
                        key={t}
                        className="text-sm px-3 py-1 rounded-full bg-[#F2EFE9] text-[#2D2926]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {card.expertise_areas?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#9A938A] mb-3">
                    Expertise
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {card.expertise_areas.map((t) => (
                      <span
                        key={t}
                        className="text-sm px-3 py-1 rounded-full bg-[#F2EFE9] text-[#2D2926]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          <div className="surface-card p-10 bg-[#F2EFE9] text-center">
            <h2 className="font-serif text-3xl text-[#2D2926] mb-3">
              Build your own Colleague.
            </h2>
            <p className="text-[#6B655D] mb-6 max-w-md mx-auto">
              Talk through the day, get advice, get honest feedback. Take 30 seconds to start.
            </p>
            <Link
              to="/register"
              data-testid="public-card-cta-bottom"
              className="inline-flex items-center gap-2 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-7 py-3.5 text-sm font-medium transition-colors"
            >
              Get started — it's free
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </main>

      <footer className="border-t border-[#E6E2D8] py-6 text-center text-xs text-[#9A938A] tracking-wider uppercase">
        A Colleague Card · colleague.app
      </footer>
    </div>
  );
}
